import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, Outlet } from "react-router-dom";
import { Menu, X as XIcon } from "lucide-react";

import clubbieLogo from "../../assets/images/clubbie_logo_full.svg";
import TournamentBadge from "../../components/TournamentBadge/TournamentBadge";
import { getTournamentById } from "../../redux/slices/publicTournamentSlice";
import {
  selectTournament,
  clearAllSelections,
} from "../../redux/slices/tournamentSlice";
import { ReactComponent as FixturesIcon } from "../../assets/images/icons/fixtures.svg";
import { ReactComponent as StandingsIcon } from "../../assets/images/icons/standings.svg";
import { ReactComponent as StatisticsIcon } from "../../assets/images/icons/statistics.svg";
import { PublicProvider } from "../../context/PublicContext";
import { publicViewViewed } from "../../utils/analytics";

const PublicDashboardLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { tournamentId } = useParams();

  const { selectedTournament: publicTournament, loading } = useSelector(
    (state) => state.publicTournament
  );
  const { selectedTournament } = useSelector((state) => state.tournament);

  const tournamentLogo = selectedTournament?.logo;

  const [hasFetched, setHasFetched] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Fire PUBLIC_VIEW_VIEWED once per tournament visit (on layout mount).
  useEffect(() => {
    if (tournamentId) publicViewViewed({ tournamentId });
  }, [tournamentId]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch tournament on mount and populate tournament slice
  useEffect(() => {
    if (!tournamentId) return;

    dispatch(getTournamentById(tournamentId))
      .unwrap()
      .then((data) => {
        if (data?.length > 0) {
          dispatch(selectTournament(data[0]));
        }
        setHasFetched(true);
      })
      .catch(() => {
        setHasFetched(true);
      });

    return () => {
      dispatch(clearAllSelections());
    };
  }, [dispatch, tournamentId]);

  const sidebarItems = useMemo(
    () => [
      {
        title: "Fixtures",
        icon: <FixturesIcon />,
        path: `/public/${tournamentId}/fixtures`,
      },
      {
        title: "Standings",
        icon: <StandingsIcon />,
        path: `/public/${tournamentId}/standings`,
      },
      {
        title: "Statistics",
        icon: <StatisticsIcon />,
        path: `/public/${tournamentId}/statistics`,
      },
    ],
    [tournamentId]
  );

  if (loading && !hasFetched) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a1128] text-[#FEFCEB]">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#0a1128] text-[#FEFCEB] max-w-[1512px] mx-auto w-full">
      {/* Topbar - Full Width */}
      <div className="w-full flex justify-between items-center px-3 py-3 md:px-6 md:py-4 border-b border-[#0D1B29] bg-[#000F1F] z-[100]">
        <div className="flex items-center gap-3">
          <button
            className="md:hidden p-1 text-[#FEFCEB] hover:text-[#00E7FF] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <XIcon size={22} /> : <Menu size={22} />}
          </button>
          <img
            className="h-auto w-[100px] md:w-[150px] cursor-pointer"
            src={clubbieLogo}
            alt="clubbie logo"
            onClick={() => navigate(`/public/${tournamentId}/fixtures`)}
          />
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[200] flex md:hidden">
          <div className="w-[280px] bg-[#000F1F] flex flex-col h-full border-r border-[#0D1B29] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {/* Tournament Display */}
            <div className="mx-4 my-1">
              <div className="flex items-center gap-2 p-2 mt-2">
                <div className="w-8 h-8 rounded-lg shrink-0 overflow-hidden">
                  <TournamentBadge src={tournamentLogo} alt="comp logo" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 flex flex-col">
                  <span className="text-base font-semibold text-[#FEFCEB] leading-tight text-s">{selectedTournament?.name || "Loading..."}</span>
                  <span className="text-xs text-[#7F8585] leading-tight">Competition</span>
                </div>
              </div>
            </div>
            {/* Menu Items */}
            <div className="flex-1 flex flex-col overflow-y-auto mx-3 mt-2 [&::-webkit-scrollbar]:hidden">
              <div className="flex-1">
                {sidebarItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <div
                      key={index}
                      className={`w-full h-10 flex px-3 items-center gap-2 cursor-pointer text-[14px] font-normal my-0.5 [&_svg]:w-5 [&_svg]:h-[18px] [&_svg]:shrink-0 ${
                        isActive
                          ? "bg-[#001A2A] text-[#00E7FF] rounded-full [&_svg_path]:stroke-[#00E7FF] [&_svg_circle]:stroke-[#00E7FF] [&_svg_rect]:stroke-[#00E7FF]"
                          : "text-[#7F8585] rounded-lg hover:bg-[#001A2A] hover:text-[#00E7FF] hover:rounded-full [&:hover_svg_path]:stroke-[#00E7FF] [&:hover_svg_circle]:stroke-[#00E7FF] [&:hover_svg_rect]:stroke-[#00E7FF]"
                      }`}
                      onClick={() => navigate(item.path)}
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
        </div>
      )}

      {/* Bottom Section with Sidebar and Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Desktop only */}
        <div className="hidden md:flex w-[280px] border-r border-[#0D1B29] bg-[#000F1F] flex-col [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* Tournament Display (static, no dropdown) */}
          <div className="mx-4 my-1">
            <div className="flex items-center gap-2 p-2 mt-2">
              <div className="w-8 h-8 rounded-lg shrink-0 overflow-hidden">
                <TournamentBadge src={tournamentLogo} alt="comp logo" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 flex flex-col">
                <span className="text-base font-semibold text-[#FEFCEB] leading-tight text-s">{selectedTournament?.name || "Loading..."}</span>
                <span className="text-xs text-[#7F8585] leading-tight">Competition</span>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="flex-1 flex flex-col overflow-y-auto mx-3 mt-2 [&::-webkit-scrollbar]:hidden">
            <div className="flex-1">
              {sidebarItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <div
                    key={index}
                    className={`w-full h-10 flex px-3 items-center gap-2 cursor-pointer text-[14px] font-normal my-0.5 [&_svg]:w-5 [&_svg]:h-[18px] [&_svg]:shrink-0 ${
                      isActive
                        ? "bg-[#001A2A] text-[#00E7FF] rounded-full [&_svg_path]:stroke-[#00E7FF] [&_svg_circle]:stroke-[#00E7FF] [&_svg_rect]:stroke-[#00E7FF]"
                        : "text-[#7F8585] rounded-lg hover:bg-[#001A2A] hover:text-[#00E7FF] hover:rounded-full [&:hover_svg_path]:stroke-[#00E7FF] [&:hover_svg_circle]:stroke-[#00E7FF] [&:hover_svg_rect]:stroke-[#00E7FF]"
                    }`}
                    onClick={() => navigate(item.path)}
                  >
                    {item.icon}
                    <span>{item.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 bg-[#000F1F] overflow-y-auto relative [&::-webkit-scrollbar]:hidden">
          <PublicProvider>
            <Outlet />
          </PublicProvider>
        </div>
      </div>
    </div>
  );
};

export default PublicDashboardLayout;
